import React, { useMemo, useState } from 'react';
import Button from '../../ui/Button';
import MediaCreatorAiTrace from './MediaCreatorAiTrace';
import MediaCreatorAiRefine from './MediaCreatorAiRefine';
import MediaAssetCarousel from './MediaAssetCarousel';
import { registryLabel } from './mediaCreatorDomRegistry';
import {
  editableTextStyleFromCss,
  patchEditableTextStyle,
  patchRegionBackground,
  regionBackgroundFromCss,
} from './mediaCreatorDocumentCss';

const TABS = [
  { id: 'selection', label: 'Selection' },
  { id: 'style', label: 'Style' },
  { id: 'ai', label: 'AI' },
];

const MediaCreatorPropertiesPanel = ({
  layout,
  brief,
  data,
  selectedElementKey,
  selectedPhotoSlot,
  photoSlotTool,
  setPhotoSlotTool,
  registryEntries,
  pickTarget,
  setPickTarget,
  meta,
  suggestedLogos,
  suggestedPhotos,
  onResetElementLayout,
  onDeleteElement,
  onPatchLayout,
  onApplyPhotoSlot,
  onApplyLogo,
  onPhotoZoomChange,
  onRefine,
  refineBusy,
  onRegenerate,
  regenerateBusy,
  showSlotCaptions,
  onShowSlotCaptionsChange,
  selectedFontSizePx,
}) => {
  const [tab, setTab] = useState('selection');

  const logoSelected = selectedElementKey === 'logo:brand' || pickTarget === 'logo';
  const regionKey = selectedElementKey?.startsWith('region:') ? selectedElementKey : null;
  const editableKey = selectedElementKey && !selectedElementKey.includes(':')
    ? selectedElementKey
    : null;

  const regionBg = useMemo(
    () => regionBackgroundFromCss(layout?.document_css, regionKey),
    [layout?.document_css, regionKey],
  );

  const textStyle = useMemo(
    () => editableTextStyleFromCss(layout?.document_css, editableKey),
    [layout?.document_css, editableKey],
  );

  const selectionSummary = selectedElementKey
    ? registryLabel(selectedElementKey, registryEntries)
    : selectedPhotoSlot
      ? (layout.photo_slots || []).find((s) => s.id === selectedPhotoSlot)?.label || selectedPhotoSlot
      : 'Nothing selected';

  const updateCss = (nextCss) => {
    onPatchLayout((l) => ({ ...l, document_css: nextCss }), `css:${selectedElementKey || 'document'}`);
  };

  const confirmRegenerate = () => {
    if (window.confirm('Re-run AI will replace the layout from your brief. Save first if you need a backup. Continue?')) {
      onRegenerate?.();
    }
  };

  return (
    <aside className="mc-props">
      <div className="mc-props-header">
        <h4>Properties</h4>
        <p className="mc-props-selection">{selectionSummary}</p>
      </div>

      <div className="mc-props-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`mc-props-tab${tab === t.id ? ' on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'selection' && (
        <div className="mc-props-panel">
          <label className="mc-field mc-field-check">
            <input
              type="checkbox"
              checked={showSlotCaptions}
              onChange={(e) => onShowSlotCaptionsChange?.(e.target.checked)}
            />
            Show slot labels (editor only)
          </label>

          {editableKey && (
            <p className="dd-muted mc-props-hint">
              Edit this text on the artboard (click the field, then type). Line breaks you add there are saved
              in the layout HTML.
            </p>
          )}

          {selectedElementKey && (
            <div className="mc-props-actions">
              <Button size="sm" variant="ghost" onClick={onResetElementLayout}>Reset position</Button>
              <Button size="sm" variant="ghost" onClick={() => onDeleteElement?.(selectedElementKey)}>
                Delete element
              </Button>
            </div>
          )}

          {logoSelected && (
            <>
              <p className="dd-muted">Replace brand image from Brand Library</p>
              <Button size="sm" variant="secondary" onClick={() => setPickTarget('logo')}>
                Browse library…
              </Button>
              <MediaAssetCarousel
                role="logo"
                preset={data.preset}
                query={meta.logo_query || 'Selkirk Copper logo'}
                selectedId={layout.logo_binding}
                suggested={suggestedLogos}
                onSelect={onApplyLogo}
                onSearchMore={() => setPickTarget('logo')}
              />
            </>
          )}

          {selectedPhotoSlot && (
            <>
              <div className="mc-photo-slot-tools" role="group" aria-label="Photo slot tools">
                <Button
                  size="sm"
                  variant={photoSlotTool === 'crop' ? 'primary' : 'ghost'}
                  onClick={() => setPhotoSlotTool('crop')}
                >
                  Adjust crop
                </Button>
                <Button
                  size="sm"
                  variant={photoSlotTool === 'move' ? 'primary' : 'ghost'}
                  onClick={() => setPhotoSlotTool('move')}
                >
                  Move slot
                </Button>
              </div>
              {layout.photo_bindings?.[selectedPhotoSlot] != null && (
                <div className="mc-props-actions mc-photo-zoom">
                  <span className="dd-muted">
                    Zoom {Math.round((layout.photo_zoom?.[selectedPhotoSlot] ?? 1) * 100)}% · Ctrl + scroll on the photo
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={(layout.photo_zoom?.[selectedPhotoSlot] ?? 1) === 1}
                    onClick={() => onPhotoZoomChange?.(selectedPhotoSlot, 1)}
                  >
                    Reset zoom
                  </Button>
                </div>
              )}
              <Button size="sm" variant="secondary" onClick={() => setPickTarget(selectedPhotoSlot)}>
                Replace from library
              </Button>
              <MediaAssetCarousel
                role="photo"
                preset={data.preset}
                brief={data.brief}
                selectedId={layout.photo_bindings?.[selectedPhotoSlot]}
                suggested={suggestedPhotos}
                onSelect={(id, asset) => onApplyPhotoSlot?.(selectedPhotoSlot, id, asset)}
                onSearchMore={() => setPickTarget(selectedPhotoSlot)}
              />
            </>
          )}
        </div>
      )}

      {tab === 'style' && (
        <div className="mc-props-panel">
          {regionKey && (
            <>
              <p className="dd-muted">Background for {registryLabel(regionKey, registryEntries)}</p>
              <div className="mc-field">
                <label className="mc-field-label">Fill</label>
                <select
                  className="mc-props-select"
                  value={regionBg.mode}
                  onChange={(e) => {
                    const mode = e.target.value;
                    updateCss(patchRegionBackground(layout.document_css, regionKey, { ...regionBg, mode }));
                  }}
                >
                  <option value="none">Theme default</option>
                  <option value="solid">Solid color</option>
                  <option value="gradient">Linear gradient</option>
                </select>
              </div>
              {regionBg.mode === 'solid' && (
                <div className="mc-field">
                  <label className="mc-field-label">Color</label>
                  <input
                    type="color"
                    value={/^#/.test(regionBg.color) ? regionBg.color : '#1A1A1A'}
                    onChange={(e) => {
                      updateCss(patchRegionBackground(layout.document_css, regionKey, {
                        ...regionBg,
                        mode: 'solid',
                        color: e.target.value,
                      }));
                    }}
                  />
                </div>
              )}
              {regionBg.mode === 'gradient' && (
                <>
                  <div className="mc-field">
                    <label className="mc-field-label">Angle (deg)</label>
                    <input
                      type="number"
                      className="mc-props-input"
                      value={regionBg.angle}
                      onChange={(e) => {
                        updateCss(patchRegionBackground(layout.document_css, regionKey, {
                          ...regionBg,
                          mode: 'gradient',
                          angle: Number(e.target.value) || 135,
                        }));
                      }}
                    />
                  </div>
                  <div className="mc-field mc-field-row">
                    <input
                      type="color"
                      value={/^#/.test(regionBg.stopA) ? regionBg.stopA : '#F9BE0B'}
                      onChange={(e) => {
                        updateCss(patchRegionBackground(layout.document_css, regionKey, {
                          ...regionBg,
                          mode: 'gradient',
                          stopA: e.target.value,
                        }));
                      }}
                    />
                    <input
                      type="color"
                      value={/^#/.test(regionBg.stopB) ? regionBg.stopB : '#1A1A1A'}
                      onChange={(e) => {
                        updateCss(patchRegionBackground(layout.document_css, regionKey, {
                          ...regionBg,
                          mode: 'gradient',
                          stopB: e.target.value,
                        }));
                      }}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {editableKey && (
            <>
              <p className="dd-muted">Text style for {editableKey}</p>
              <div className="mc-field">
                <label className="mc-field-label">Color</label>
                <input
                  type="color"
                  value={/^#/.test(textStyle.color) ? textStyle.color : '#FFFFFF'}
                  onChange={(e) => {
                    updateCss(patchEditableTextStyle(layout.document_css, editableKey, {
                      ...textStyle,
                      color: e.target.value,
                      fontSizePx: textStyle.fontSizePx,
                    }));
                  }}
                />
              </div>
              <div className="mc-field">
                <label className="mc-field-label">Font size (px)</label>
                <input
                  type="number"
                  className="mc-props-input"
                  min={8}
                  max={120}
                  value={textStyle.fontSizePx || ''}
                  placeholder={selectedFontSizePx ? `${selectedFontSizePx} (current)` : 'inherit'}
                  onChange={(e) => {
                    updateCss(patchEditableTextStyle(layout.document_css, editableKey, {
                      color: textStyle.color,
                      fontSizePx: e.target.value,
                    }));
                  }}
                />
              </div>
              {textStyle.fontSizePx ? (
                <p className="dd-muted mc-props-hint">Clear the field to return to the design's size.</p>
              ) : null}
            </>
          )}

          {!regionKey && !editableKey && (
            <p className="dd-muted">Select a text field or region (copy column, gallery, invite) to style it.</p>
          )}
        </div>
      )}

      {tab === 'ai' && (
        <div className="mc-props-panel">
          <MediaCreatorAiRefine
            selectedElementKey={selectedElementKey}
            registryEntries={registryEntries}
            onApply={onRefine}
            busy={refineBusy}
          />
          <hr className="mc-props-divider" />
          <MediaCreatorAiTrace
            layout={layout}
            brief={brief}
            onRegenerate={confirmRegenerate}
            regenerating={regenerateBusy}
          />
        </div>
      )}
    </aside>
  );
};

export default MediaCreatorPropertiesPanel;
